'use client'

import { HomepageQueryResult, PageQueryResult } from "@/sanity.types";
import { useRef, useState, useEffect } from "react";
import ColorScheme from "./colorScheme";
import { AnimatePresence, motion } from "framer-motion";

type QuoteMarqueesProps = Extract<NonNullable<NonNullable<HomepageQueryResult>["content"]>[number], { _type: "quoteMarquees" }> | Extract<NonNullable<NonNullable<PageQueryResult>["content"]>[number], { _type: "quoteMarquees" }>;
type QuoteMarqueeProps = NonNullable<QuoteMarqueesProps["quotes"]>[0]

const QuoteMarquee = ({ quoteMarquee } : { quoteMarquee : QuoteMarqueeProps }) => {
  const {quote, quoteAuthor} = quoteMarquee;
  const marqueeRef = useRef<HTMLDivElement>(null);
  const marqueeQuoteRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflowing = () => {
      const container = marqueeRef.current;
      const quoteEl = marqueeQuoteRef.current;
      if (!container || !quoteEl) return;

      if (!window.matchMedia('(min-width: 768px)').matches) {
        setIsOverflowing(false);
        return;
      }

      setIsOverflowing(quoteEl.scrollWidth > container.clientWidth);
    };

    const scheduleCheck = () => requestAnimationFrame(checkOverflowing);

    scheduleCheck();
    void document.fonts?.ready?.then(scheduleCheck);

    const container = marqueeRef.current;
    const observer = container ? new ResizeObserver(scheduleCheck) : undefined;
    if (container && observer) {
      observer.observe(container);
    }

    window.addEventListener('resize', scheduleCheck);

    return () => {
      window.removeEventListener('resize', scheduleCheck);
      observer?.disconnect();
    };
  }, [quote]);

  const handleMouseOver = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (event.currentTarget.classList.contains('marqueePaused')) {
      event.currentTarget.classList.remove('marqueePaused')
    }
  }

  const handleMouseOut = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (!event.currentTarget.classList.contains('marqueePaused')) {
      event.currentTarget.classList.add('marqueePaused')
    }
  }

  return (
    <div className={`border-t pt-8 pb-5 md:grid md:grid-cols-4 md:gap-x-[0.9375rem] md:pt-10 md:pb-6${isOverflowing ? ` relative` : ``}`}>
      {quoteAuthor && <p className="mb-5">{quoteAuthor}</p>}
      {quote &&
        <div
          ref={marqueeRef}
          onMouseOver={isOverflowing ? handleMouseOver : undefined}
          onMouseOut={isOverflowing ? handleMouseOut : undefined}
          className={`col-span-3 min-w-0 w-full${isOverflowing ? ` md:overflow-hidden md:flex marquee marqueePaused` : ``}`}
        >
          <h3
            ref={marqueeQuoteRef}
            className={`h4 md:h3 md:whitespace-nowrap${isOverflowing ? ` md:shrink-0 md:w-min` : ``}`}
          >
            {quote}{' '}
          </h3>
          {isOverflowing &&
            <h3 className="h4 md:h3 md:shrink-0 md:whitespace-nowrap">{quote} </h3>
          }
        </div>
      }
      {isOverflowing &&
        <span
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-[30px] bg-linear-to-r from-transparent to-(--color-background) md:block"
        />
      }
    </div>
  )
}

export default function QuoteMarquees(props: QuoteMarqueesProps) {
  const {heading, quotes, colorScheme} = props;
  const [showingMore, setShowingMore] = useState(false);
  const handleClick = () => {
    setShowingMore(true);
  }

  return (
    <section className="quote-marquees">
      <ColorScheme colorScheme={colorScheme && colorScheme != 'default'? colorScheme : 'royal'}>
        <div className="bg-(--color-background) text-(--color-foreground)">
          <div className="p-5 md:p-[1.875rem] marketing-container">
            {heading && <p className="eyebrow mb-[1.875rem]">{heading}</p>}
            {quotes &&
              <div className="mb-[1.875rem]">
                {quotes.map((quoteMarquee, i) =>
                  i < 3 && <QuoteMarquee key={i} quoteMarquee={quoteMarquee} />
                )}
                <AnimatePresence>
                  {showingMore &&
                    <motion.div
                      initial={{ height: 0 }}
                      animate={{ height: 'auto' }}
                      exit={{ height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      {quotes.map((quoteMarquee, i) =>
                        i >= 3 && <QuoteMarquee key={i} quoteMarquee={quoteMarquee} />
                      )}
                    </motion.div>
                  }
                </AnimatePresence>
                {quotes.length > 3 && !showingMore && <p onClick={handleClick} className="eyebrow underline underline-offset-2 text-right cursor-pointer">Load More</p>}
              </div>
            }
          </div>
        </div>
      </ColorScheme>
    </section>
  );
}